import { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';

type Row = Record<string, unknown>;

const formatDateTime = (v: unknown): string => {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDate = (v: unknown): string => {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const txt = (v: unknown): string => (v === null || v === undefined || v === '' ? '—' : String(v));

export interface SectionConfig {
  /** cabeçalhos da tabela de detalhes */
  columns: { key: string; label: string; render?: (row: Row) => ReactNode }[];
  /** texto quando não há itens */
  emptyLabel?: string;
}

/**
 * Registry de seções do front — espelha os providers do backend.
 * Para plugar um módulo novo: adicione uma entrada com a `key` igual à do
 * provider do backend. Sem entrada, a aba cai num render genérico.
 */
export const sections: Record<string, SectionConfig> = {
  okrs: {
    columns: [
      { key: 'okr', label: 'OKR', render: (r) => txt(r.okr) },
      { key: 'tipo', label: 'Ação', render: (r) => <Badge variant="outline">{txt(r.tipo)}</Badge> },
      { key: 'descricao', label: 'Descrição', render: (r) => txt(r.descricao) },
      { key: 'data', label: 'Data', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhuma atividade em OKRs registrada para este usuário.',
  },
  linha_do_tempo: {
    columns: [
      { key: 'data', label: 'Quando', render: (r) => formatDateTime(r.data) },
      { key: 'modulo', label: 'Módulo', render: (r) => <Badge variant="outline">{txt(r.modulo)}</Badge> },
      { key: 'descricao', label: 'O que fez', render: (r) => txt(r.descricao) },
    ],
    emptyLabel: 'Nenhuma ação registrada para este usuário.',
  },
  calendario: {
    columns: [
      { key: 'titulo', label: 'Evento', render: (r) => txt(r.titulo) },
      { key: 'calendario', label: 'Calendário', render: (r) => txt(r.calendario) },
      { key: 'status', label: 'Status', render: (r) => <Badge variant="outline">{txt(r.status)}</Badge> },
      { key: 'papel', label: 'Papel', render: (r) => txt(r.papel) },
      { key: 'data_inicio', label: 'Início', render: (r) => formatDateTime(r.data_inicio) },
      { key: 'data_fim', label: 'Fim', render: (r) => formatDateTime(r.data_fim) },
    ],
    emptyLabel: 'Nenhum evento criado ou com participação deste usuário.',
  },
  cronogramas: {
    columns: [
      { key: 'anexo', label: 'Anexo', render: (r) => txt(r.anexo) },
      { key: 'atividade', label: 'Atividade', render: (r) => txt(r.atividade) },
      { key: 'cronograma', label: 'Projeto', render: (r) => txt(r.cronograma) },
      { key: 'data', label: 'Enviado em', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhum anexo enviado por este usuário em projetos.',
  },
  projetos: {
    columns: [
      { key: 'titulo', label: 'Projeto', render: (r) => txt(r.titulo) },
      { key: 'papel', label: 'Papel', render: (r) => <Badge variant="outline">{txt(r.papel)}</Badge> },
      { key: 'status', label: 'Status', render: (r) => txt(r.status) },
      { key: 'empresa', label: 'Empresa', render: (r) => txt(r.empresa) },
      { key: 'data', label: 'Criado em', render: (r) => formatDate(r.data) },
    ],
    emptyLabel: 'Nenhum projeto liderado ou criado por este usuário.',
  },
  organogramas: {
    columns: [
      { key: 'nome', label: 'Organograma', render: (r) => txt(r.nome) },
      { key: 'papel', label: 'Papel', render: (r) => <Badge variant="outline">{txt(r.papel)}</Badge> },
      { key: 'status', label: 'Status', render: (r) => txt(r.status) },
      { key: 'data', label: 'Atualizado em', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhum organograma criado ou editado por este usuário.',
  },
  fluxogramas: {
    columns: [
      { key: 'nome', label: 'Fluxograma', render: (r) => txt(r.nome) },
      { key: 'papel', label: 'Papel', render: (r) => <Badge variant="outline">{txt(r.papel)}</Badge> },
      { key: 'status', label: 'Status', render: (r) => txt(r.status) },
      { key: 'data', label: 'Atualizado em', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhum fluxograma criado ou editado por este usuário.',
  },
  treinamentos: {
    columns: [
      { key: 'curso', label: 'Curso', render: (r) => txt(r.curso) },
      { key: 'aula', label: 'Aula concluída', render: (r) => txt(r.aula) },
      { key: 'data', label: 'Concluída em', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhuma aula concluída por este usuário.',
  },
  formularios: {
    columns: [
      { key: 'formulario', label: 'Formulário', render: (r) => txt(r.formulario) },
      { key: 'data', label: 'Respondido em', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhum formulário respondido por este usuário.',
  },
  notificacoes: {
    columns: [
      { key: 'titulo', label: 'Título', render: (r) => txt(r.titulo) },
      { key: 'tipo', label: 'Tipo', render: (r) => <Badge variant="outline">{txt(r.tipo)}</Badge> },
      {
        key: 'lida',
        label: 'Status',
        render: (r) => (
          <Badge variant={r.lida ? 'secondary' : 'default'}>{r.lida ? 'Lida' : 'Não lida'}</Badge>
        ),
      },
      { key: 'data', label: 'Recebida em', render: (r) => formatDateTime(r.data) },
    ],
    emptyLabel: 'Nenhuma notificação para este usuário.',
  },
  acessos: {
    columns: [
      { key: 'empresa', label: 'Empresa', render: (r) => txt(r.empresa) },
      { key: 'departamento', label: 'Departamento', render: (r) => txt(r.departamento) },
      { key: 'tipo', label: 'Perfil', render: (r) => <Badge variant="outline">{txt(r.tipo)}</Badge> },
      {
        key: 'ativo',
        label: 'Situação',
        render: (r) => (
          <Badge variant={r.ativo ? 'default' : 'secondary'}>{r.ativo ? 'Ativo' : 'Inativo'}</Badge>
        ),
      },
    ],
    emptyLabel: 'Usuário sem vínculos de empresa.',
  },
  auditoria: {
    columns: [
      { key: 'data', label: 'Data/Hora', render: (r) => formatDateTime(r.data) },
      { key: 'ip', label: 'IP', render: (r) => txt(r.ip) },
      { key: 'user_agent', label: 'Dispositivo / Navegador', render: (r) => txt(r.user_agent) },
    ],
    emptyLabel: 'Nenhum acesso registrado para este usuário.',
  },
};

/** fallback genérico: renderiza as chaves do objeto que não forem 'id' */
export const genericColumns = (row: Row): SectionConfig['columns'] =>
  Object.keys(row)
    .filter((k) => k !== 'id')
    .map((k) => ({ key: k, label: k, render: (r: Row) => txt(r[k]) }));
